import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
// Import SiriDharini logo
import logoImage from '../../../assets/images/siridharinilogo.jpeg';

const AdminPanelHeader = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const [screenWidth, setScreenWidth] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();
  const headerRef = useRef(null);
  
  // Get logout function from AuthContext
  const { logout, user } = useAuth();

  // SiriDharini Admin Navigation Items - With Blog Management
  const navigationItems = [
    {
      name: 'Content Management',
      href: '/adminportal/content',
      hasDropdown: true,
      active: location.pathname.startsWith('/adminportal/content') || location.pathname.startsWith('/adminportal/siridharini-blog'),
      dropdownItems: [
        { name: 'Blog Upload', href: '/adminportal/siridharini-blog-upload' },
        { name: 'Manage Blogs', href: '/adminportal/siridharini-blogs' }
      ]
    },
    {
      name: 'User Management',
      href: '/adminportal/users',
      hasDropdown: true,
      active: location.pathname.startsWith('/adminportal/signup'),
      dropdownItems: [
        { name: 'Admin Signup', href: '/adminportal/signup' },
        { name: 'Admin Logout', href: 'LOGOUT', isButton: true, isLogout: true },
      ]
    },
    { name: 'Admin Logout', href: 'LOGOUT', isButton: true, isLogout: true }
  ];

  // Handle screen size detection
  useEffect(() => {
    const updateScreenSize = () => {
      const width = window.innerWidth;
      setScreenWidth(width);
      setIsMobile(width < 1024);
      
      // Close mobile menu when switching to desktop
      if (width >= 1024) {
        setIsMenuOpen(false);
        setActiveDropdown(null);
      }
    };

    // Initial check
    updateScreenSize();

    // Add event listener
    window.addEventListener('resize', updateScreenSize);
    
    // Cleanup
    return () => window.removeEventListener('resize', updateScreenSize);
  }, []);

  // Handle contact clicks
  const handleEmailClick = useCallback((event) => {
    event.preventDefault();
    window.location.href = 'mailto:connect@siridharini.com';
  }, []);

  const handlePhoneClick = useCallback((event) => {
    event.preventDefault();
    window.location.href = 'tel:+919448472706';
  }, []);

  // Handle dropdown toggle
  const handleDropdownToggle = useCallback((itemName, event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    setActiveDropdown(prev => prev === itemName ? null : itemName);
  }, []);

  // Secure logout function that integrates with AuthContext
  const handleLogout = useCallback(() => {
    // Show confirmation dialog
    const confirmLogout = window.confirm(`Are you sure you want to logout${user?.name ? `, ${user.name}` : ''}?`);
    if (!confirmLogout) return;

    try {
      console.log('SiriDharini admin logout initiated');
      
      // Close all menus first
      setIsMenuOpen(false);
      setActiveDropdown(null);

      // Use AuthContext logout (this will clear the auth state and localStorage)
      logout();
      
      // Additional cleanup for any admin-specific data
      localStorage.removeItem('adminSession');
      localStorage.removeItem('authToken');
      
      // Clear session storage as well
      sessionStorage.clear();
      
      console.log('Logout completed, navigating to login');

      // Navigate to login page with replace to prevent back button access
      navigate('/admin', { 
        replace: true,
        state: { fromLogout: true, message: 'You have been successfully logged out.' }
      });

      // Show logout message after navigation
      setTimeout(() => {
        alert('You have been successfully logged out.');
      }, 500);

    } catch (error) {
      console.error('Logout error:', error);
      // Still navigate to login even if there's an error
      logout(); // Ensure AuthContext is cleared
      navigate('/admin', { replace: true });
    }
  }, [navigate, logout, user]);

  // Navigation handler with improved logout logic
  const handleNavigation = useCallback((href, event, isLogout = false) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    // Handle logout specifically
    if (isLogout || href === 'LOGOUT') {
      handleLogout();
      return;
    }

    if (!href) return;

    // Close all menus
    setIsMenuOpen(false);
    setActiveDropdown(null);

    // Navigate
    if (href !== location.pathname) {
      navigate(href);
    }

    // Scroll to top
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 100);
  }, [navigate, location.pathname, handleLogout]);

  // Handle mobile menu toggle
  const handleMobileMenuToggle = useCallback((event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    setIsMenuOpen(prev => !prev);
    setActiveDropdown(null);
  }, []);

  // Handle outside clicks and cleanup
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (headerRef.current && !headerRef.current.contains(event.target)) {
        setActiveDropdown(null);
        if (isMobile) {
          setIsMenuOpen(false);
        }
      }
    };

    const handleEscapeKey = (event) => {
      if (event.key === 'Escape') {
        setActiveDropdown(null);
        setIsMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscapeKey);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [isMobile]);

  // Close menus when route changes
  useEffect(() => {
    setActiveDropdown(null);
    setIsMenuOpen(false);
  }, [location.pathname]);

  // Menu icon component
  const MenuIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M3 12h18M3 6h18M3 18h18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
    </svg>
  );

  const CloseIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
    </svg>
  );

  const ChevronDownIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );

  return (
    <>
      {/* Top Contact Bar */}
      <div style={{
        backgroundColor: '#1a2332',
        color: '#ffffff',
        fontSize: screenWidth < 480 ? '0.75rem' : screenWidth < 768 ? '0.8rem' : '0.875rem',
        padding: screenWidth < 480 ? '8px 0' : '12px 0',
        borderBottom: '1px solid #4a5568',
        display: screenWidth < 360 ? 'none' : 'block'
      }}>
        <div style={{
          maxWidth: '1400px',
          margin: '0 auto',
          padding: `0 ${screenWidth < 480 ? '0.75rem' : screenWidth < 768 ? '1rem' : '1.5rem'}`,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: screenWidth < 480 ? '0.5rem' : '1rem'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: screenWidth < 480 ? '1rem' : screenWidth < 768 ? '2rem' : '3rem',
            flexWrap: 'wrap'
          }}>
            {/* Interactive Email Button */}
            <button
              onClick={handleEmailClick}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                color: '#ffffff',
                fontSize: screenWidth < 480 ? '0.7rem' : '0.875rem',
                fontWeight: '400',
                padding: '0.25rem 0.5rem',
                borderRadius: '4px',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.1)';
                e.target.style.color = '#22c55e';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = 'transparent';
                e.target.style.color = '#ffffff';
              }}
              title="Send us an email"
            >
              <span>📧</span>
              <span>connect@siridharini.com</span>
            </button>

            {/* Interactive Phone Button */}
            <button
              onClick={handlePhoneClick}
              style={{
                display: screenWidth < 480 ? 'none' : 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                color: '#ffffff',
                fontSize: screenWidth < 480 ? '0.7rem' : '0.875rem',
                fontWeight: '400',
                padding: '0.25rem 0.5rem',
                borderRadius: '4px',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.1)';
                e.target.style.color = '#22c55e';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = 'transparent';
                e.target.style.color = '#ffffff';
              }}
              title="Call us"
            >
              <span>📞</span>
              <span>+91 9448472706</span>
            </button>
          </div>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '1rem'
          }}>
            {/* User info display */}
            {user && (
              <div style={{
                fontSize: screenWidth < 480 ? '0.7rem' : '0.875rem',
                color: '#22c55e',
                fontWeight: '500',
                display: screenWidth < 640 ? 'none' : 'block'
              }}>
                Welcome, {user.name || user.username}
              </div>
            )}
            <div style={{
              fontWeight: '500',
              fontSize: screenWidth < 480 ? '0.7rem' : '0.875rem',
              color: '#22c55e',
              textAlign: 'center',
              display: screenWidth < 640 ? 'none' : 'block'
            }}>
              Premium Quality Organic Products
            </div>
          </div>
        </div>
      </div>

      {/* Main Navigation */}
      <nav 
        ref={headerRef}
        style={{
          backgroundColor: 'white',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
          position: 'sticky',
          top: 0,
          zIndex: 1000,
          borderBottom: '1px solid #f0f0f0'
        }}
      >
        <div style={{
          maxWidth: '1400px',
          margin: '0 auto',
          padding: `0 ${screenWidth < 480 ? '0.75rem' : screenWidth < 768 ? '1rem' : '2rem'}`
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            height: screenWidth < 480 ? '60px' : screenWidth < 768 ? '65px' : '70px',
            minHeight: '60px'
          }}>
            {/* Logo Section */}
            <div style={{ 
              display: 'flex', 
              alignItems: 'center',
              flex: 1,
              minWidth: 0,
              maxWidth: isMobile ? 'calc(100% - 60px)' : 'auto'
            }}>
              <button
                onClick={(e) => handleNavigation('/adminportal', e)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: screenWidth < 480 ? '0.25rem' : '0.5rem',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '0.25rem',
                  minWidth: 0,
                  overflow: 'hidden',
                  width: '100%'
                }}
              >
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: screenWidth < 480 ? '0.25rem' : '0.5rem',
                  minWidth: 0,
                  width: '100%'
                }}>
                  {/* SiriDharini Logo Image */}
                  <img 
                    src={logoImage} 
                    alt="SiriDharini Admin Logo" 
                    style={{
                      width: screenWidth < 480 ? '35px' : screenWidth < 768 ? '40px' : '50px',
                      height: screenWidth < 480 ? '35px' : screenWidth < 768 ? '40px' : '50px',
                      objectFit: 'contain',
                      flexShrink: 0,
                      borderRadius: '8px'
                    }}
                  />
                  
                  <div style={{ 
                    textAlign: 'left',
                    minWidth: 0,
                    overflow: 'hidden',
                    flex: 1
                  }}>
                    <div style={{
                      fontSize: screenWidth < 380 ? '14px' : screenWidth < 480 ? '16px' : screenWidth < 768 ? '18px' : '20px',
                      fontWeight: '600',
                      lineHeight: '1.1',
                      letterSpacing: '0.5px',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis'
                    }}>
                      <span style={{ color: '#000000' }}>Siri</span>
                      <span style={{ color: '#22c55e' }}>Dharini</span>
                    </div>
                    <div style={{
                      fontSize: screenWidth < 380 ? '8px' : screenWidth < 480 ? '9px' : screenWidth < 768 ? '10px' : '11px',
                      color: '#666666',
                      fontWeight: '400',
                      marginTop: '1px',
                      letterSpacing: '0.3px',
                      display: screenWidth < 380 ? 'none' : 'block',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis'
                    }}>
                      Admin Management System
                    </div>
                    <div style={{
                      fontSize: screenWidth < 480 ? '7px' : screenWidth < 768 ? '9px' : '10px',
                      color: '#22c55e',
                      fontWeight: '500',
                      marginTop: '1px',
                      letterSpacing: '0.2px',
                      display: screenWidth < 480 ? 'none' : 'block',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis'
                    }}>
                      Organic & Sustainable Products
                    </div>
                  </div>
                </div>
              </button>
            </div>

            {/* Desktop Navigation - Only show on desktop */}
            {!isMobile && (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: screenWidth < 1200 ? '1rem' : '2rem'
              }}>
                {navigationItems.map((item) => (
                  <div key={item.name} style={{ position: 'relative' }}>
                    {item.isButton ? (
                      <button
                        onClick={(e) => handleNavigation(item.href, e, item.isLogout)}
                        style={{
                          backgroundColor: 'transparent',
                          color: '#dc2626',
                          padding: '0.5rem 1rem',
                          borderRadius: '25px',
                          border: '2px solid #dc2626',
                          fontWeight: '600',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          fontSize: screenWidth < 1200 ? '12px' : '14px',
                          letterSpacing: '0.3px',
                          whiteSpace: 'nowrap'
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.backgroundColor = '#dc2626';
                          e.target.style.color = 'white';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.backgroundColor = 'transparent';
                          e.target.style.color = '#dc2626';
                        }}
                      >
                        Admin Logout →
                      </button>
                    ) : (
                      <>
                        <button
                          onClick={item.hasDropdown ? 
                            (e) => handleDropdownToggle(item.name, e) : 
                            (e) => handleNavigation(item.href, e)
                          }
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.25rem',
                            padding: '0.5rem 0',
                            fontWeight: '500',
                            fontSize: screenWidth < 1200 ? '13px' : '15px',
                            color: item.active ? '#22c55e' : '#333333',
                            backgroundColor: 'transparent',
                            border: 'none',
                            transition: 'color 0.2s ease',
                            cursor: 'pointer',
                            letterSpacing: '0.3px',
                            whiteSpace: 'nowrap'
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.color = '#22c55e';
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.color = item.active ? '#22c55e' : '#333333';
                          }}
                        >
                          <span>{item.name}</span>
                          {item.hasDropdown && (
                            <div style={{
                              transform: activeDropdown === item.name ? 'rotate(180deg)' : 'rotate(0deg)',
                              transition: 'transform 0.2s ease'
                            }}>
                              <ChevronDownIcon />
                            </div>
                          )}
                        </button>

                        {/* Desktop Dropdown */}
                        {item.hasDropdown && activeDropdown === item.name && (
                          <div
                            style={{
                              position: 'absolute',
                              top: '100%',
                              left: 0,
                              minWidth: '200px',
                              backgroundColor: 'white',
                              boxShadow: '0 8px 25px rgba(0, 0, 0, 0.12)',
                              borderRadius: '8px',
                              padding: '0.75rem',
                              zIndex: 1000,
                              border: '1px solid #f0f0f0',
                              marginTop: '0.5rem'
                            }}
                          >
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                              {item.dropdownItems.map((dropItem) => (
                                <button
                                  key={dropItem.name}
                                  onClick={(e) => handleNavigation(dropItem.href, e, dropItem.isLogout)}
                                  style={{
                                    display: 'block',
                                    width: '100%',
                                    padding: '0.75rem 1rem',
                                    color: dropItem.isLogout ? '#dc2626' : '#333333',
                                    fontSize: '14px',
                                    fontWeight: '500',
                                    transition: 'all 0.2s ease',
                                    border: 'none',
                                    backgroundColor: 'transparent',
                                    cursor: 'pointer',
                                    textAlign: 'left',
                                    borderRadius: '6px'
                                  }}
                                  onMouseEnter={(e) => {
                                    if (dropItem.isLogout) {
                                      e.target.style.backgroundColor = '#fef2f2';
                                      e.target.style.color = '#dc2626';
                                    } else {
                                      e.target.style.backgroundColor = '#f0fdf4';
                                      e.target.style.color = '#22c55e';
                                    }
                                  }}
                                  onMouseLeave={(e) => {
                                    e.target.style.backgroundColor = 'transparent';
                                    e.target.style.color = dropItem.isLogout ? '#dc2626' : '#333333';
                                  }}
                                >
                                  {dropItem.name}
                                </button>
                              ))}
                            </div>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Mobile Menu Button - Only show on mobile */}
            {isMobile && (
              <button
                onClick={handleMobileMenuToggle}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: screenWidth < 480 ? '35px' : '40px',
                  height: screenWidth < 480 ? '35px' : '40px',
                  background: 'none',
                  border: '2px solid #f0f0f0',
                  borderRadius: '6px',
                  color: '#333333',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  flexShrink: 0
                }}
                onMouseEnter={(e) => {
                  e.target.style.borderColor = '#22c55e';
                  e.target.style.color = '#22c55e';
                }}
                onMouseLeave={(e) => {
                  e.target.style.borderColor = '#f0f0f0';
                  e.target.style.color = '#333333';
                }}
              >
                {isMenuOpen ? <CloseIcon /> : <MenuIcon />}
              </button>
            )}
          </div>

          {/* Mobile Navigation - Only show on mobile when menu is open */}
          {isMobile && isMenuOpen && (
            <div style={{
              backgroundColor: 'white',
              borderTop: '1px solid #f0f0f0',
              padding: '1rem 0',
              display: 'block',
              maxHeight: '70vh',
              overflowY: 'auto',
              overflowX: 'hidden'
            }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                {/* Mobile Contact Information */}
                <div style={{
                  padding: '1rem',
                  backgroundColor: '#1a2332',
                  borderRadius: '8px',
                  marginBottom: '1rem'
                }}>
                  {/* User info for mobile */}
                  {user && (
                    <div style={{
                      color: '#22c55e',
                      fontSize: '14px',
                      fontWeight: '600',
                      marginBottom: '0.5rem',
                      padding: '0.5rem',
                      backgroundColor: 'rgba(34, 197, 94, 0.1)',
                      borderRadius: '4px'
                    }}>
                      Welcome, {user.name || user.username}
                    </div>
                  )}
                  
                  <button
                    onClick={handleEmailClick}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      color: '#ffffff',
                      fontSize: '14px',
                      fontWeight: '400',
                      padding: '0.5rem',
                      borderRadius: '4px',
                      width: '100%',
                      marginBottom: '0.5rem',
                      transition: 'all 0.2s ease'
                    }}
                  >
                    <span>📧</span>
                    <span>connect@siridharini.com</span>
                  </button>
                  
                  <button
                    onClick={handlePhoneClick}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      color: '#ffffff',
                      fontSize: '14px',
                      fontWeight: '400',
                      padding: '0.5rem',
                      borderRadius: '4px',
                      width: '100%',
                      transition: 'all 0.2s ease'
                    }}
                  >
                    <span>📞</span>
                    <span>+91 9448472706</span>
                  </button>
                </div>

                {navigationItems.map((item) => (
                  <div key={item.name} style={{ width: '100%' }}>
                    {item.isButton ? (
                      <button
                        onClick={(e) => handleNavigation(item.href, e, item.isLogout)}
                        style={{
                          width: '100%',
                          backgroundColor: 'transparent',
                          color: '#dc2626',
                          padding: '1rem',
                          borderRadius: '8px',
                          border: '2px solid #dc2626',
                          fontWeight: '600',
                          marginTop: '1rem',
                          cursor: 'pointer',
                          fontSize: '14px'
                        }}
                      >
                        {item.name}
                      </button>
                    ) : (
                      <>
                        <button
                          onClick={item.hasDropdown ? 
                            (e) => handleDropdownToggle(item.name, e) : 
                            (e) => handleNavigation(item.href, e)
                          }
                          style={{
                            width: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            padding: '0.75rem 1rem',
                            borderRadius: '6px',
                            backgroundColor: item.active ? '#f0fdf4' : 'transparent',
                            border: 'none',
                            fontWeight: '500',
                            fontSize: '15px',
                            color: item.active ? '#22c55e' : '#333333',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease'
                          }}
                        >
                          <span style={{ textAlign: 'left', flex: 1 }}>
                            {item.name}
                          </span>
                          {item.hasDropdown && (
                            <div style={{
                              transform: activeDropdown === item.name ? 'rotate(180deg)' : 'rotate(0deg)',
                              transition: 'transform 0.2s ease'
                            }}>
                              <ChevronDownIcon />
                            </div>
                          )}
                        </button>

                        {/* Mobile Dropdown */}
                        {item.hasDropdown && activeDropdown === item.name && (
                          <div 
                            style={{
                              marginLeft: '1rem',
                              marginTop: '0.5rem',
                              paddingLeft: '1rem',
                              borderLeft: '2px solid #f0f0f0'
                            }}
                          >
                            {item.dropdownItems.map((dropItem) => (
                              <button
                                key={dropItem.name}
                                onClick={(e) => handleNavigation(dropItem.href, e, dropItem.isLogout)}
                                style={{
                                  display: 'block',
                                  width: '100%',
                                  padding: '0.75rem 1rem',
                                  fontSize: '14px',
                                  fontWeight: '500',
                                  color: dropItem.isLogout ? '#dc2626' : '#666666',
                                  borderRadius: '4px',
                                  marginBottom: '0.25rem',
                                  border: 'none',
                                  backgroundColor: 'transparent',
                                  cursor: 'pointer',
                                  textAlign: 'left',
                                  transition: 'all 0.2s ease'
                                }}
                              >
                                {dropItem.name}
                              </button>
                            ))}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </nav>
    </>
  );
};

export default AdminPanelHeader;